import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OptimizationRecommendations = ({ vessel, recommendations }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [implementedActions, setImplementedActions] = useState(new Set());

  const categories = [
    { value: 'all', label: 'All Recommendations', icon: 'List' },
    { value: 'speed', label: 'Speed Optimization', icon: 'Gauge' },
    { value: 'route', label: 'Route Planning', icon: 'Route' },
    { value: 'fuel', label: 'Fuel Management', icon: 'Fuel' },
    { value: 'maintenance', label: 'Maintenance', icon: 'Wrench' }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'border-error/20 bg-error/5 text-error';
      case 'medium':
        return 'border-warning/20 bg-warning/5 text-warning';
      case 'low':
        return 'border-success/20 bg-success/5 text-success';
      default:
        return 'border-border bg-background text-foreground';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return 'AlertCircle';
      case 'medium':
        return 'AlertTriangle';
      case 'low':
        return 'Info';
      default:
        return 'Circle';
    }
  };

  const getImpactColor = (impact) => {
    if (impact >= 15) return 'text-success';
    if (impact >= 8) return 'text-warning';
    return 'text-muted-foreground';
  };

  const handleImplement = (recommendationId) => {
    const newImplemented = new Set(implementedActions);
    if (newImplemented?.has(recommendationId)) {
      newImplemented?.delete(recommendationId);
    } else {
      newImplemented?.add(recommendationId);
    }
    setImplementedActions(newImplemented);
  };

  const filteredRecommendations = selectedCategory === 'all' 
    ? recommendations 
    : recommendations?.filter(rec => rec?.category === selectedCategory);

  const totalPotentialSavings = recommendations?.reduce((sum, rec) => sum + rec?.potentialSavings, 0);
  const implementedSavings = recommendations?.filter(rec => implementedActions?.has(rec?.id))?.reduce((sum, rec) => sum + rec?.potentialSavings, 0);

  return (
    <div className="bg-card rounded-lg border p-6 shadow-maritime-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Optimization Recommendations</h3>
          <p className="text-sm text-muted-foreground">AI-powered suggestions for performance improvement</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <div className="text-sm text-muted-foreground">Potential Savings</div>
            <div className="text-lg font-bold text-success">
              ${totalPotentialSavings?.toLocaleString()}/month
            </div>
          </div>
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export Report
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {categories?.map((category) => (
          <Button
            key={category?.value}
            variant={selectedCategory === category?.value ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setSelectedCategory(category?.value)}
            iconName={category?.icon}
            iconPosition="left"
          >
            {category?.label}
          </Button>
        ))}
      </div>
      <div className="space-y-4">
        {filteredRecommendations?.map((recommendation) => (
          <div 
            key={recommendation?.id}
            className={`p-4 rounded-lg border transition-maritime ${
              implementedActions?.has(recommendation?.id) 
                ? 'border-success/20 bg-success/5' 
                : getPriorityColor(recommendation?.priority)
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <Icon 
                    name={getPriorityIcon(recommendation?.priority)} 
                    size={16} 
                    strokeWidth={2}
                    className={implementedActions?.has(recommendation?.id) ? 'text-success' : ''}
                  />
                  <h4 className="font-semibold text-foreground">
                    {recommendation?.title}
                  </h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    recommendation?.priority === 'high' ? 'bg-error/10 text-error' :
                    recommendation?.priority === 'medium'? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                  }`}>
                    {recommendation?.priority?.toUpperCase()}
                  </span>
                </div>
                
                <p className="text-sm text-muted-foreground mb-3">
                  {recommendation?.description}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                  <div className="flex items-center space-x-2">
                    <Icon name="DollarSign" size={14} className="text-success" strokeWidth={2} />
                    <span className="text-sm text-muted-foreground">Savings:</span>
                    <span className="text-sm font-medium text-success">
                      ${recommendation?.potentialSavings?.toLocaleString()}/month
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Icon name="TrendingDown" size={14} className="text-accent" strokeWidth={2} />
                    <span className="text-sm text-muted-foreground">CO₂ Reduction:</span>
                    <span className={`text-sm font-medium ${getImpactColor(recommendation?.co2Impact)}`}>
                      {recommendation?.co2Impact}%
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={14} className="text-muted-foreground" strokeWidth={2} />
                    <span className="text-sm text-muted-foreground">Implementation:</span>
                    <span className="text-sm font-medium text-foreground">
                      {recommendation?.implementationTime}
                    </span>
                  </div>
                </div>
                
                {recommendation?.steps && (
                  <div className="mt-3">
                    <div className="text-sm font-medium text-foreground mb-2">Implementation Steps:</div>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {recommendation?.steps?.map((step, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <span className="text-accent font-medium">{index + 1}.</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              <div className="flex flex-col space-y-2 ml-4">
                <Button
                  variant={implementedActions?.has(recommendation?.id) ? 'success' : 'outline'}
                  size="sm"
                  onClick={() => handleImplement(recommendation?.id)}
                  iconName={implementedActions?.has(recommendation?.id) ? 'Check' : 'Play'}
                  iconPosition="left"
                >
                  {implementedActions?.has(recommendation?.id) ? 'Implemented' : 'Implement'}
                </Button>
                
                <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="right">
                  Details
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {implementedActions?.size > 0 && (
        <div className="mt-6 p-4 bg-success/5 border border-success/20 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon name="CheckCircle" size={20} className="text-success" strokeWidth={2} />
              <div>
                <div className="text-sm font-medium text-foreground">
                  {implementedActions?.size} recommendation{implementedActions?.size > 1 ? 's' : ''} implemented
                </div>
                <div className="text-sm text-muted-foreground">
                  Projected monthly savings: ${implementedSavings?.toLocaleString()}
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" iconName="BarChart3" iconPosition="left">
              Track Progress
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OptimizationRecommendations;